'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useCreateTemplate } from '@/features/playbooks/api/use-playbook-templates';
import { Nis2WorkflowTemplateMatrix } from '@/features/playbooks/components/Nis2WorkflowTemplateMatrix';
import { TemplateBuilderForm } from '@/features/playbooks/components/TemplateBuilderForm';
import { WorkflowTemplateOverview } from '@/features/playbooks/components/WorkflowTemplateOverview';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import type { CreatePhaseInput, PlaybookDomain } from '@/features/playbooks/types';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/tasks/playbooks/templates/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.createWorkflowTemplate'),
  }),
  component: CreateTemplatePage,
});

function CreateTemplatePage() {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const { createTemplate, isCreating, isOrgReady } = useCreateTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [domain, setDomain] = useState<PlaybookDomain>('custom');
  const [phases, setPhases] = useState<CreatePhaseInput[]>([]);

  const handleSave = async () => {
    if (!name.trim()) return;
    if (!isOrgReady) {
      toast.error(t('tasks:toast.noActiveOrganizationPleaseSelectAnOrganizationFirst'));
      return;
    }
    // Client-side validation: check that all phases and obligations have names.
    for (const phase of phases) {
      if (!phase.name.trim()) {
        toast.error(t('tasks:toast.allPhasesMustHaveAName'));
        return;
      }
      for (const obl of phase.obligations) {
        if (!obl.name.trim()) {
          toast.error(t('tasks:toast.allObligationsInPhaseMustHaveAName2', { phase: phase.name }));
          return;
        }
      }
    }
    try {
      const result = await createTemplate({
        name: name.trim(),
        description: description.trim() || null,
        domain,
        metadata: {},
        phases,
      });
      toast.success(t('tasks:toast.hasBeenCreated2', { result: result.name }));
      void navigate({
        to: '/tasks/playbooks/templates/$templateId',
        params: { templateId: result.id },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('newTemplate.failedToCreate');
      toast.error(message);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <WorkflowSectionTabs activeTab="templates" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void navigate({ to: '/tasks/playbooks/templates' })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('newTemplate.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('newTemplate.description')}</p>
          </div>
        </div>
        <Button
          onClick={() => void handleSave()}
          disabled={isCreating || !name.trim() || !isOrgReady}
          data-testid="save-template-btn"
        >
          {isCreating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {t('newTemplate.saveTemplate')}
        </Button>
      </div>

      {domain !== 'nis2' && phases.length > 0 ? <WorkflowTemplateOverview phases={phases} /> : null}

      {domain === 'nis2' ? <Nis2WorkflowTemplateMatrix /> : null}

      {domain !== 'nis2' ? (
        <TemplateBuilderForm
          name={name}
          description={description}
          domain={domain}
          phases={phases}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onDomainChange={setDomain}
          onPhasesChange={setPhases}
          readOnly={false}
        />
      ) : null}
    </div>
  );
}
